import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MessageSquare, Send, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentChat = () => {
  const [messages, setMessages] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { isApproved } = useAuth();

  useEffect(() => {
    fetchChat();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchChat({ silent: true });
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChat = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await axios.get(API_URL + '/api/student/chat');
      setMessages(response.data.messages || []);
      setTeacher(response.data.teacher);
    } catch (err) {
      if (!silent) {
        console.error('Failed to load chat:', err);
        toast.error('Failed to load chat');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(API_URL + '/api/student/chat/messages', { content: newMessage.trim() });
      await fetchChat({ silent: true });
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => new Date(dateString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="student-chat-page">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          {isApproved ? 'Chat with Teacher' : 'Support Chat'}
        </h1>
        <p className="text-[#1A2E6E]/60 mt-1">
          {isApproved
            ? 'Direct communication with your teacher'
            : 'Use this chat to ask questions while your account is pending approval'}
        </p>
      </div>

      {!isApproved && (
        <div className="mb-4 rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/10 px-4 py-3 text-sm text-[#1A2E6E]/80">
          Your account is still under review. Classes and activities stay locked until approval, but you can chat with our team here for onboarding and support.
        </div>
      )}
      
      <div className="flex-1 bg-white rounded-xl border border-[#E8EAF0] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#E8EAF0] flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#D72B2B]/10 flex items-center justify-center">
            {teacher ? (
              <span className="text-lg font-semibold text-[#D72B2B]">{teacher.name?.charAt(0)}</span>
            ) : (
              <User className="w-6 h-6 text-[#D72B2B]" />
            )}
          </div>
          <div>
            <p className="font-semibold text-[#1A2E6E]">{teacher?.name || (isApproved ? 'Your Teacher' : 'Support Team')}</p>
            <p className="text-sm text-[#1A2E6E]/60">{teacher?.email || 'Frankly Talking English'}</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAF7]/50">
          {messages.length > 0 ? (
            messages.map((msg, index) => {
              const isStudent = msg.sender_role === 'student';
              const showDate = index === 0 || formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
              return (
                <React.Fragment key={msg.id}>
                  {showDate && (
                    <div className="text-center">
                      <span className="text-xs text-[#1A2E6E]/50 bg-white px-3 py-1 rounded-full">{formatDate(msg.created_at)}</span>
                    </div>
                  )}
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    className={isStudent ? 'flex justify-end' : 'flex justify-start'}
                  >
                    <div className={isStudent 
                      ? 'max-w-[75%] rounded-2xl px-4 py-3 bg-[#1A2E6E] text-white' 
                      : 'max-w-[75%] rounded-2xl px-4 py-3 bg-white text-[#1A2E6E]'
                    }>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <span className={isStudent ? 'text-xs text-white/60' : 'text-xs text-[#1A2E6E]/50'}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  </motion.div>
                </React.Fragment>
              );
            })
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8">
                <MessageSquare className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-3" />
                <p className="text-[#1A2E6E]/60">
                  {isApproved
                    ? 'No messages yet. Send a message to start.'
                    : 'No support messages yet. Send a message and our team will reply here.'}
                </p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="p-4 border-t border-[#E8EAF0]">
          <div className="flex gap-3">
            <input 
              type="text" 
              value={newMessage} 
              onChange={(e) => setNewMessage(e.target.value)} 
              placeholder={isApproved ? 'Type your message...' : 'Ask about onboarding, approval, or support...'} 
              data-testid="student-chat-message-input"
              className="flex-1 px-4 py-3 rounded-xl border border-[#E8EAF0] focus:border-[#1A2E6E] outline-none" 
              disabled={sending} 
            />
            <button 
              type="submit" 
              disabled={!newMessage.trim() || sending} 
              data-testid="student-chat-send-btn"
              className="px-6 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] disabled:opacity-50 flex items-center gap-2"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentChat;
